import { isImpersonationLaunchId } from './messageTransport';

export const IMPERSONATION_BOOTSTRAP_KEY = '__CAPITIUS_IMPERSONATION_BOOTSTRAP__';

const ROUTES = Object.freeze({
  '/impersonation/loading': { kind: 'parent', phase: 'loading' },
  '/impersonation': { kind: 'parent', phase: 'exchange' },
  '/impersonation/start-failed': { kind: 'parent', phase: 'start-failed' },
  '/impersonation/terminal': { kind: 'parent', phase: 'terminal' },
  '/impersonation/teacher/loading': { kind: 'teacher', phase: 'loading' },
  '/impersonation/teacher': { kind: 'teacher', phase: 'exchange' },
  '/impersonation/teacher/start-failed': { kind: 'teacher', phase: 'start-failed' },
  '/impersonation/teacher/terminal': { kind: 'teacher', phase: 'terminal' },
});

const normalizePathname = (pathname) => {
  if (typeof pathname !== 'string' || !pathname.startsWith('/')) return '';
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
};

const readSingleLaunchId = (search) => {
  if (typeof search !== 'string') return '';
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
  const launchIds = params.getAll('launch');
  return launchIds.length === 1 && isImpersonationLaunchId(launchIds[0])
    ? launchIds[0]
    : '';
};

export const readImpersonationBootstrap = ({ location, history } = {}) => {
  const pathname = normalizePathname(location?.pathname);
  if (!pathname.startsWith('/impersonation')) return null;

  const route = ROUTES[pathname] ?? { kind: null, phase: 'invalid' };
  const launchId = route.phase === 'loading' ? readSingleLaunchId(location?.search) : '';

  // Fragments are never a supported credential transport. Remove any fragment
  // before React, authentication, analytics, or ordinary application modules load.
  if (location?.hash) {
    history?.replaceState?.(
      history.state,
      '',
      `${location?.pathname || pathname}${location?.search || ''}`,
    );
  }

  return Object.freeze({
    ...route,
    pathname,
    launchId,
    hasLaunchId: Boolean(launchId),
  });
};

export const exposeImpersonationBootstrap = (bootstrap) => {
  if (typeof window === 'undefined') return;

  if (bootstrap) {
    const { kind, phase, pathname } = bootstrap;
    window[IMPERSONATION_BOOTSTRAP_KEY] = Object.freeze({ kind, phase, pathname });
  } else {
    delete window[IMPERSONATION_BOOTSTRAP_KEY];
  }
};

export const clearCopiedImpersonationSessionStorage = () => {
  if (typeof window === 'undefined') return;

  try {
    // window.open may clone the opener's sessionStorage. The service window
    // must start without administrator-scoped feature or authentication data.
    window.sessionStorage?.clear();
  } catch {
    // Storage is optional; authentication still remains memory-only.
  }
};

export const getImpersonationBootstrap = () => (
  typeof window === 'undefined'
    ? null
    : window[IMPERSONATION_BOOTSTRAP_KEY] ?? null
);

export const isImpersonationRuntime = () => Boolean(getImpersonationBootstrap());

export default readImpersonationBootstrap;
